/**
 * SanskritTranslator — Open-source model translation to Sanskrit
 *
 * Calls the HuggingFace Inference API directly from the browser.
 * Supported models:
 *   • Helsinki-NLP/opus-mt-hi-sa       — Hindi → Sanskrit
 *   • facebook/nllb-200-distilled-600M — multilingual → Sanskrit (san_Deva)
 *   • facebook/nllb-200-1.3B           — multilingual → Sanskrit (larger)
 *   • ai4bharat/indictrans2-*          — backend-only (listed for awareness)
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import '../styles/SanskritTranslator.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SourceLang {
  code: string;
  label: string;
  nllbCode: string;
}

interface TranslationModel {
  id: string;
  name: string;
  hfModel: string;
  description: string;
  supportedSources: string[];
  apiType: 'opus' | 'nllb' | 'backend-only';
  size: string;
  quality: 'good' | 'better' | 'best';
}

interface HistoryEntry {
  id: string;
  sourceLang: string;
  sourceText: string;
  result: string;
  modelId: string;
  ts: number;
}

// ─── Static data ──────────────────────────────────────────────────────────────

const SOURCE_LANGS: SourceLang[] = [
  { code: 'hi', label: 'हिंदी',       nllbCode: 'hin_Deva' },
  { code: 'en', label: 'English',     nllbCode: 'eng_Latn' },
  { code: 'mr', label: 'मराठी',       nllbCode: 'mar_Deva' },
  { code: 'bn', label: 'বাংলা',       nllbCode: 'ben_Beng' },
  { code: 'gu', label: 'ગુજરાતી',     nllbCode: 'guj_Gujr' },
  { code: 'ta', label: 'தமிழ்',       nllbCode: 'tam_Taml' },
  { code: 'te', label: 'తెలుగు',      nllbCode: 'tel_Telu' },
  { code: 'kn', label: 'ಕನ್ನಡ',       nllbCode: 'kan_Knda' },
  { code: 'ml', label: 'മലയാളം',      nllbCode: 'mal_Mlym' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ',      nllbCode: 'pan_Guru' },
];

const MODELS: TranslationModel[] = [
  {
    id: 'opus-hi-sa',
    name: 'OPUS-MT hi→sa',
    hfModel: 'Helsinki-NLP/opus-mt-hi-sa',
    description: 'Helsinki-NLP lightweight Hindi→Sanskrit model trained on the OPUS corpus. Fast, runs on CPU.',
    supportedSources: ['hi'],
    apiType: 'opus',
    size: '~300 MB',
    quality: 'good',
  },
  {
    id: 'nllb-600m',
    name: 'NLLB-200 Distilled 600M',
    hfModel: 'facebook/nllb-200-distilled-600M',
    description: "Meta's No Language Left Behind — 200 languages incl. Sanskrit (san_Deva). Good balance of speed and quality.",
    supportedSources: ['hi', 'en', 'mr', 'bn', 'gu', 'ta', 'te', 'kn', 'ml', 'pa'],
    apiType: 'nllb',
    size: '~2.4 GB',
    quality: 'better',
  },
  {
    id: 'nllb-1.3b',
    name: 'NLLB-200 1.3B',
    hfModel: 'facebook/nllb-200-1.3B',
    description: 'Larger NLLB variant — higher translation quality, slower cold start on free tier.',
    supportedSources: ['hi', 'en', 'mr', 'bn', 'gu', 'ta', 'te', 'kn', 'ml', 'pa'],
    apiType: 'nllb',
    size: '~5.2 GB',
    quality: 'best',
  },
  {
    id: 'indictrans2-indic',
    name: 'IndicTrans2 Indic→Indic 200M',
    hfModel: 'ai4bharat/indictrans2-indic-indic-dist-200M',
    description: 'AI4Bharat best-in-class Indic multilingual model. Requires backend deployment (trust_remote_code).',
    supportedSources: ['hi', 'mr', 'bn', 'gu', 'ta', 'te', 'kn', 'ml', 'pa'],
    apiType: 'backend-only',
    size: '~800 MB',
    quality: 'best',
  },
];

const QUALITY_META: Record<string, { emoji: string; label: string }> = {
  good:   { emoji: '⚡', label: 'Fast'    },
  better: { emoji: '⚖️', label: 'Balanced' },
  best:   { emoji: '✨', label: 'Best'    },
};

const HF_API = 'https://api-inference.huggingface.co/models';
const LS_API_KEY  = 'st_hf_apikey';
const LS_HISTORY  = 'st_translate_history';
const MAX_HISTORY = 20;
const MAX_RETRIES = 4;
const RETRY_DELAY = 6000;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function loadHistory(): HistoryEntry[] {
  try {
    return JSON.parse(localStorage.getItem(LS_HISTORY) ?? '[]');
  } catch { return []; }
}

function saveHistory(h: HistoryEntry[]) {
  localStorage.setItem(LS_HISTORY, JSON.stringify(h.slice(0, MAX_HISTORY)));
}

function langLabel(code: string) {
  return SOURCE_LANGS.find(l => l.code === code)?.label ?? code;
}

function modelName(id: string) {
  return MODELS.find(m => m.id === id)?.name ?? id;
}

function relTime(ts: number): string {
  const m = Math.round((Date.now() - ts) / 60000);
  if (m < 1)  return 'अभी';
  if (m < 60) return `${m}मि पहले`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}घं पहले`;
  return `${Math.floor(h / 24)}दि पहले`;
}

// ─── API call ─────────────────────────────────────────────────────────────────

async function callHF(
  model: TranslationModel,
  text: string,
  srcLang: SourceLang,
  apiKey: string,
  attempt = 0
): Promise<string> {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (apiKey) headers['Authorization'] = `Bearer ${apiKey}`;

  const body = model.apiType === 'nllb'
    ? { inputs: text, parameters: { src_lang: srcLang.nllbCode, tgt_lang: 'san_Deva' } }
    : { inputs: text };

  const res = await fetch(`${HF_API}/${model.hfModel}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (res.status === 503) {
    const data = await res.json();
    if (data?.error?.includes('loading') && attempt < MAX_RETRIES) {
      await new Promise(r => setTimeout(r, RETRY_DELAY));
      return callHF(model, text, srcLang, apiKey, attempt + 1);
    }
    throw new Error(`Model warming up — estimated ${Math.round(data.estimated_time ?? RETRY_DELAY / 1000)}s. Try again shortly.`);
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error ?? `HTTP ${res.status}`);
  }

  const data = await res.json();
  const translation = Array.isArray(data)
    ? data[0]?.translation_text
    : data?.translation_text;
  if (!translation) throw new Error('Empty response from model.');
  return translation;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  onNavigate?: (path: string) => void;
}

export default function SanskritTranslator({ onNavigate }: Props) {
  const [sourceLangCode, setSourceLangCode] = useState('hi');
  const [modelId, setModelId]               = useState('nllb-600m');
  const [inputText, setInputText]           = useState('');
  const [output, setOutput]                 = useState('');
  const [status, setStatus]                 = useState<'idle' | 'loading' | 'retrying' | 'error' | 'done'>('idle');
  const [statusMsg, setStatusMsg]           = useState('');
  const [history, setHistory]               = useState<HistoryEntry[]>(loadHistory);
  const [apiKey, setApiKey]                 = useState(() => localStorage.getItem(LS_API_KEY) ?? '');
  const [showApiKey, setShowApiKey]         = useState(false);
  const [showHistory, setShowHistory]       = useState(false);
  const [copied, setCopied]                 = useState(false);
  const abortRef = useRef(false);

  const sourceLang = SOURCE_LANGS.find(l => l.code === sourceLangCode)!;
  const availableModels = MODELS.filter(m => m.supportedSources.includes(sourceLangCode));
  const selectedModel = MODELS.find(m => m.id === modelId) ?? availableModels[0];

  // When source lang changes, reset model if current doesn't support it
  useEffect(() => {
    if (!selectedModel?.supportedSources.includes(sourceLangCode)) {
      const fallback = availableModels.find(m => m.apiType !== 'backend-only');
      if (fallback) setModelId(fallback.id);
    }
  }, [sourceLangCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveApiKey = useCallback((key: string) => {
    setApiKey(key);
    localStorage.setItem(LS_API_KEY, key);
  }, []);

  const translate = useCallback(async () => {
    if (!inputText.trim() || !selectedModel) return;
    if (selectedModel.apiType === 'backend-only') {
      setStatus('error');
      setStatusMsg('This model requires backend deployment. Select a different model.');
      return;
    }

    abortRef.current = false;
    setStatus('loading');
    setStatusMsg('Calling model…');
    setOutput('');

    try {
      const result = await callHF(selectedModel, inputText.trim(), sourceLang, apiKey);
      if (abortRef.current) return;
      setOutput(result);
      setStatus('done');
      setStatusMsg('');

      const entry: HistoryEntry = {
        id: Math.random().toString(36).slice(2),
        sourceLang: sourceLangCode,
        sourceText: inputText.trim(),
        result,
        modelId: selectedModel.id,
        ts: Date.now(),
      };
      setHistory(prev => {
        const updated = [entry, ...prev].slice(0, MAX_HISTORY);
        saveHistory(updated);
        return updated;
      });
    } catch (e: unknown) {
      if (abortRef.current) return;
      setStatus('error');
      setStatusMsg(e instanceof Error ? e.message : 'Translation failed.');
    }
  }, [inputText, selectedModel, sourceLang, sourceLangCode, apiKey]);

  const copyOutput = useCallback(() => {
    navigator.clipboard.writeText(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }, [output]);

  const clearAll = useCallback(() => {
    abortRef.current = true;
    setInputText('');
    setOutput('');
    setStatus('idle');
    setStatusMsg('');
  }, []);

  const restoreFromHistory = useCallback((entry: HistoryEntry) => {
    setSourceLangCode(entry.sourceLang);
    setModelId(entry.modelId);
    setInputText(entry.sourceText);
    setOutput(entry.result);
    setStatus('done');
    setShowHistory(false);
  }, []);

  const isLoading = status === 'loading' || status === 'retrying';

  return (
    <div className="strans-root">
      {/* ── Header ── */}
      <div className="strans-header">
        {onNavigate && (
          <button className="strans-back" onClick={() => onNavigate('/')}>← वापस</button>
        )}
        <div className="strans-header-text">
          <h1 className="strans-title">🕉 संस्कृत अनुवाद</h1>
          <p className="strans-subtitle">Open-source model translation to Sanskrit</p>
        </div>
        <div className="strans-header-actions">
          <button
            className="strans-icon-btn"
            onClick={() => setShowHistory(h => !h)}
            title="Translation history"
          >
            📜 {history.length > 0 && <span className="strans-badge">{history.length}</span>}
          </button>
          <button
            className="strans-icon-btn"
            onClick={() => setShowApiKey(s => !s)}
            title="HuggingFace API key"
          >
            🔑
          </button>
        </div>
      </div>

      {/* ── API Key panel ── */}
      {showApiKey && (
        <div className="strans-apikey-panel">
          <label className="strans-apikey-label">
            HuggingFace API Key
            <span className="strans-apikey-hint">
              Optional — increases rate limits. Get one free at{' '}
              <a href="https://huggingface.co/settings/tokens" target="_blank" rel="noreferrer">
                huggingface.co/settings/tokens
              </a>
            </span>
          </label>
          <input
            className="strans-apikey-input"
            type="password"
            placeholder="hf_xxxxxxxxxxxx"
            value={apiKey}
            onChange={e => saveApiKey(e.target.value)}
          />
        </div>
      )}

      {/* ── History panel ── */}
      {showHistory && (
        <div className="strans-history-panel">
          <div className="strans-history-header">
            <span>Recent Translations</span>
            <button
              className="strans-history-clear"
              onClick={() => { setHistory([]); saveHistory([]); }}
            >
              Clear all
            </button>
          </div>
          {history.length === 0 ? (
            <p className="strans-history-empty">No translations yet.</p>
          ) : (
            <ul className="strans-history-list">
              {history.map(entry => (
                <li
                  key={entry.id}
                  className="strans-history-item"
                  onClick={() => restoreFromHistory(entry)}
                >
                  <div className="strans-history-meta">
                    <span className="strans-history-lang">{langLabel(entry.sourceLang)}</span>
                    <span className="strans-history-model">{modelName(entry.modelId)}</span>
                    <span className="strans-history-time">{relTime(entry.ts)}</span>
                  </div>
                  <div className="strans-history-src">{entry.sourceText}</div>
                  <div className="strans-history-res">{entry.result}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ── Source language ── */}
      <div className="strans-section">
        <label className="strans-label">स्रोत भाषा — Source Language</label>
        <div className="strans-lang-grid">
          {SOURCE_LANGS.map(lang => (
            <button
              key={lang.code}
              className={`strans-lang-btn${sourceLangCode === lang.code ? ' active' : ''}`}
              onClick={() => setSourceLangCode(lang.code)}
            >
              {lang.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Model selector ── */}
      <div className="strans-section">
        <label className="strans-label">मॉडल — Translation Model</label>
        <div className="strans-model-grid">
          {availableModels.map(m => {
            const qm = QUALITY_META[m.quality];
            const isBackend = m.apiType === 'backend-only';
            return (
              <button
                key={m.id}
                className={`strans-model-card${modelId === m.id ? ' active' : ''}${isBackend ? ' disabled' : ''}`}
                onClick={() => !isBackend && setModelId(m.id)}
                disabled={isBackend}
                title={isBackend ? 'Requires backend deployment' : m.description}
              >
                <div className="strans-model-top">
                  <span className="strans-model-name">{m.name}</span>
                  <span className={`strans-model-badge strans-badge-${m.quality}`}>
                    {qm.emoji} {qm.label}
                  </span>
                </div>
                <div className="strans-model-desc">{m.description}</div>
                <div className="strans-model-meta">
                  <span className="strans-model-size">💾 {m.size}</span>
                  <span className="strans-model-hf">🤗 {m.hfModel}</span>
                  {isBackend && <span className="strans-model-tag">Backend only</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Input ── */}
      <div className="strans-section">
        <label className="strans-label">
          इनपुट — Input
          <span className="strans-label-sub">({sourceLang.label} → संस्कृत)</span>
        </label>
        <textarea
          className="strans-input"
          placeholder={
            sourceLangCode === 'hi'
              ? 'यहाँ हिंदी में लिखें…'
              : sourceLangCode === 'en'
              ? 'Type your text in English…'
              : `Type in ${sourceLang.label}…`
          }
          value={inputText}
          onChange={e => setInputText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) translate();
          }}
          rows={5}
          disabled={isLoading}
        />
        <div className="strans-input-hint">Ctrl+Enter / ⌘+Enter to translate</div>
      </div>

      {/* ── Actions ── */}
      <div className="strans-actions">
        <button
          className="strans-translate-btn"
          onClick={translate}
          disabled={isLoading || !inputText.trim() || selectedModel?.apiType === 'backend-only'}
        >
          {isLoading ? (
            <><span className="strans-spinner" /> अनुवाद हो रहा है…</>
          ) : (
            '🕉 अनुवाद करें — Translate'
          )}
        </button>
        <button className="strans-clear-btn" onClick={clearAll} disabled={isLoading}>
          Clear
        </button>
      </div>

      {/* ── Status message ── */}
      {statusMsg && (
        <div className={`strans-status strans-status-${status}`}>
          {status === 'error' ? '⚠ ' : 'ℹ '}
          {statusMsg}
          {status === 'error' && (
            <span className="strans-status-hint">
              {' '}Free-tier models may take 20–30 s to warm up. Retry or add a HF API key.
            </span>
          )}
        </div>
      )}

      {/* ── Output ── */}
      {(output || status === 'done') && (
        <div className="strans-section">
          <div className="strans-output-header">
            <label className="strans-label">संस्कृत अनुवाद — Sanskrit Output</label>
            <button className="strans-copy-btn" onClick={copyOutput}>
              {copied ? '✓ Copied' : '⎘ Copy'}
            </button>
          </div>
          <div className="strans-output">
            {output || <span className="strans-output-empty">No output yet.</span>}
          </div>
          {output && (
            <div className="strans-output-meta">
              via {selectedModel?.hfModel} · {sourceLang.label} → संस्कृत
            </div>
          )}
        </div>
      )}

      {/* ── Model notes ── */}
      <div className="strans-notes">
        <h3 className="strans-notes-title">📌 Model Notes</h3>
        <ul className="strans-notes-list">
          <li>
            <strong>OPUS-MT</strong> is fast and lightweight but trained on limited Sanskrit data.
            Best for short, simple Hindi sentences.
          </li>
          <li>
            <strong>NLLB-200</strong> models support many source languages including Sanskrit
            as a target (<code>san_Deva</code>). Cold starts on the free HuggingFace tier
            may take 20–30 seconds.
          </li>
          <li>
            <strong>IndicTrans2</strong> produces the best quality for Indic languages but
            requires <code>trust_remote_code=True</code>. It will be available once the
            backend Sanskrit translation service is deployed.
          </li>
          <li>
            Sanskrit machine translation is an active research area. Outputs should be
            reviewed by a qualified speaker before use in learning materials.
          </li>
        </ul>
      </div>
    </div>
  );
}
